import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/flammable-waste-disposal-page.module.css'
// eslint-disable-next-line import/extensions
import flammableWaste from '../../json/flammable-waste-disposal-data.json'

const flammableServiceData = {
  name: 'Flammable Waste Disposal',
  description:
    'Compliant flammable waste disposal for businesses. We handle flammable liquids, solvents, used fuel, aerosols, adhesives, and flammable solids as DOT Class 3 / RCRA D001 ignitable waste, with full EPA documentation.',
  serviceType: 'Flammable Waste Disposal',
  url: 'https://getdimension.com/flammable-waste-disposal',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Flammable Liquid Disposal' },
    { name: 'Solvent and Paint Thinner Disposal' },
    { name: 'Used Fuel and Gasoline Disposal' },
    { name: 'Aerosol and Spray Can Disposal' },
    { name: 'Flammable Solid and Adhesive Disposal' },
  ],
}

const flammableFAQData = {
  questions: flammableWaste.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function FlammableWasteDisposal() {
  return (
    <>
      <Head>
        <title>
          Flammable Waste Disposal for Businesses — DOT Class 3 | Dimension
        </title>
        <meta
          name="description"
          content="Compliant flammable waste disposal for businesses — solvents, used fuel, aerosols, adhesives, flammable solids. DOT Class 3 & RCRA D001, instant quote, 30% less than competitor rates."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/flammable-waste-disposal" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={flammableServiceData} />
        <SchemaMarkup type="FAQPage" data={flammableFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heading={flammableWaste.pitch.heading}
          material={flammableWaste.pitch.material}
          pitchText={flammableWaste.pitch.pitchText}
          ctaLink={flammableWaste.pitch.ctaLink}
          ctaText={flammableWaste.pitch.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...flammableWaste.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...flammableWaste.benefits} />
        <CaseStudy {...flammableWaste.caseStudy} />
        <Approach {...flammableWaste.approach} />
        <FAQ {...flammableWaste.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Hazardous Waste Disposal',
              href: '/hazardous-waste-disposal',
              description:
                'Safe disposal of chemical waste, solvents, and other hazardous materials for businesses.',
            },
            {
              label: 'Paint Recycling and Disposal',
              href: '/paint-disposal',
              description:
                'Compliant pickup and recycling of latex, oil-based, and spray paint for businesses.',
            },
            {
              label: 'Oil Recycling and Used Oil Pickup',
              href: '/oil-recycling',
              description:
                'Eco-friendly used cooking, motor, and hydraulic oil recycling via removal or on-site pumping.',
            },
            {
              label: 'Business Waste Solutions',
              href: '/b2b-solutions',
              description:
                'Customized waste management for enterprises with dedicated reporting and account management.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default FlammableWasteDisposal
